import { useEffect, useState } from 'react';

import { DEFAULT_LOCALE_CODE, LOCALE_STORAGE_KEY } from '../../core/constants';
import {
  addLocaleChangeEventListener,
  dispatchLocaleChangeEvent,
  getLocalStorageLocale,
  isValidLocaleCode
} from '../../utils/locale';
import { SupportedLocaleCode } from '../../core/types';
import { useLoadUserData } from '../use-load-user-data';
import { useSaveUserData } from '../use-save-user-data';

/**
 * Handles fetching and updating the user's locale (both in local storage and the backend). Please
 * note that the logic to update IntlProvider (and cause the page to change text) is handled elsewhere.
 * @param forceLocale If set, this locale will override everything else (local storage and user data).
 *                    This is useful for users that have the global nav (you can force the locale from scene data)
 */
export const useUserLocale = (forceLocale?: string) => {
  const [locale, setLocale] = useState<SupportedLocaleCode>(getLocalStorageLocale());
  const { data: userData } = useLoadUserData();
  const { save } = useSaveUserData();

  // If the forceLocale param is set, override everything and set the locale to that value
  useEffect(() => {
    if (forceLocale && isValidLocaleCode(forceLocale) && forceLocale !== locale) {
      setLocale(forceLocale as SupportedLocaleCode);
      localStorage.setItem(LOCALE_STORAGE_KEY, forceLocale as SupportedLocaleCode);
      dispatchLocaleChangeEvent(forceLocale as SupportedLocaleCode);
    }
  }, [forceLocale, locale, save, userData]);

  // Whenever the loaded user data changes, update the locale state (unless forceLocale)
  useEffect(() => {
    if (userData && !forceLocale) {
      const userLocale = userData.futrli_locale as SupportedLocaleCode;
      if (userLocale) {
        // If user has saved locale, apply it
        setLocale(userLocale);
        localStorage.setItem(LOCALE_STORAGE_KEY, userLocale);
        dispatchLocaleChangeEvent(userLocale);
      } else {
        // If user has no saved locale, apply default locale and save it
        setLocale(DEFAULT_LOCALE_CODE);
        localStorage.setItem(LOCALE_STORAGE_KEY, DEFAULT_LOCALE_CODE);
        dispatchLocaleChangeEvent(DEFAULT_LOCALE_CODE);
        save({ futrli_locale: DEFAULT_LOCALE_CODE });
      }
    }
  }, [forceLocale, save, userData]);

  // Listen out for 'localeChange' custom events and update the locale accordingly (unless forceLocale)
  useEffect(() => {
    const unregister = addLocaleChangeEventListener((newLocale: SupportedLocaleCode) => {
      if (!forceLocale) {
        localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
        if (newLocale !== locale) {
          setLocale(newLocale);
          save({ futrli_locale: newLocale });
        }
      }
    });
    return unregister;
  }, [forceLocale, locale, save]);

  return {
    locale
  };
};
